/**
 * Адреса Gateway/BFF, которые нужны самой инфраструктуре клиента (RP-A3,
 * `evidence/platform/auth-runtime-v1-20261006.md`). Адреса сценариев живут в
 * `api/urls.ts` своих пакетов: литерал `/api/` вне файлов `urls.ts` — ошибка eslint.
 */
export const GATEWAY_URLS = {
  /** Токен антифоргери для заголовка `X-CSRF-TOKEN`; cookie-половину ставит Gateway. */
  antiforgery: '/api/auth/antiforgery',
  /** Кто вошёл. 401 — сессии нет, 403 — у пользователя нет ролей приложения. */
  me: '/auth/me',
  /** Вход: полная навигация браузера, OIDC Code + PKCE выполняет BFF. */
  login: '/auth/login',
  /** Выход: BFF гасит cookie И SSO-сессию; одной своей cookie недостаточно (D-086). */
  logout: '/auth/logout',
} as const

/** Заголовки, которые ставит transport. Имена — договор с Gateway, не стиль. */
export const TRANSPORT_HEADERS = {
  csrf: 'X-CSRF-TOKEN',
  correlation: 'X-Correlation-Id',
  idempotency: 'Idempotency-Key',
  /** Идентификатор сборки клиента, который раздаёт сервер на КАЖДЫЙ ответ (nginx входа): так вкладка узнаёт о новой версии без опроса. */
  clientBuild: 'X-Client-Build',
} as const

/** Код отказа антифоргери Gateway (`CookieSessionAntiforgery`). */
export const ANTIFORGERY_FAILED = 'ANTIFORGERY_FAILED'

/** Адрес входа с возвратом туда, где человек был. */
export function loginUrl(returnUrl: string): string {
  return `${GATEWAY_URLS.login}?returnUrl=${encodeURIComponent(returnUrl)}`
}

/**
 * Адрес тихого входа: тот же вход Gateway, но `prompt=none` — форма Keycloak не
 * показывается, ответ приходит в скрытый iframe сообщением `oauth_silent_complete`.
 */
export function silentLoginUrl(returnUrl: string): string {
  return `${loginUrl(returnUrl)}&silent=true`
}

/**
 * Реестр ресурсов Platform (RP-A2) — источник сигнала «ресурс вышел из работы»
 * для границы области (`resourceScopeCleared`, D-02 аудита AUD-P). Transport
 * читает ответы списка и карточки ресурса сам: пакетам не нужно помнить об
 * очистке, а приложению — подключать отдельный listener.
 */
export const PLATFORM_REGISTRY_URL = '/api/platform/v1/resources'

/**
 * Режим обновления РМК (RP-MNT): общий признак «идёт обновление» на всю установку. Читают все вошедшие (при старте и
 * после восстановления связи), включают — по праву. Платформенный адрес здесь, как и реестр выше: режим обновления —
 * забота оболочки, а не пакета вида.
 */
export const PLATFORM_MAINTENANCE_URL = '/api/platform/v1/maintenance'

/** Настройки раздела «Фото» (RP-CACHE-0): сроки временных загрузок и кадров поиска, потолок кэша; чтение и правка по праву. */
export const PLATFORM_PHOTO_SETTINGS_URL = '/api/platform/v1/photo/settings'

/** Пульт обновления: открытые РМК сообщают сюда своё состояние, администратор читает список. */
export const PLATFORM_MAINTENANCE_WORKPLACES_URL = '/api/platform/v1/maintenance/workplaces'

/** Одно рабочее место пульта обновления (уход РМК с экрана). */
export function platformMaintenanceWorkplaceUrl(workplaceId: string): string {
  return `${PLATFORM_MAINTENANCE_WORKPLACES_URL}/${encodeURIComponent(workplaceId)}`
}

/** Живой канал (LIVE): WebSocket того же origin через Gateway, cookie-сессия, без токенов в браузере. */
export const LIVE_PATH = '/api/platform/v1/live'

/** Адрес канала для страницы: `wss://` на https, `ws://` на http (локальная разработка). */
export function liveUrl(location: { protocol: string; host: string }): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${LIVE_PATH}`
}
